import { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Table, Card, Empty, Tag, Button, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import ChartCard from '@/components/ChartCard';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { EChartsOption } from 'echarts';
import { fetchTradeDisplay, fetchAvailableMonths, uploadBacktestExcel, TradeDay } from '@/api/mediumTermBacktest';
import PageContainer from '@/components/PageContainer';

const WINDOW_COLORS = [
  '#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5f27cd',
  '#01a3a4', '#f368e0', '#ff6348', '#7bed9f',
];

interface TableRow {
  key: string;
  target_date: string;
  actual_price: number | null;
  [key: string]: number | string | null;
}

function useMediumTermData(month: string) {
  const [days, setDays] = useState<TradeDay[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    fetchTradeDisplay(month)
      .then((res) => setDays(res.days))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month]);

  return { days, loading };
}

export default function MediumTermTradePage() {
  const isMobile = useIsMobile();
  const [months, setMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const uploadKey = useRef(0);

  const refreshMonths = () => {
    fetchAvailableMonths().then((list) => {
      setMonths(list);
      if (list.length > 0 && !selectedMonth) setSelectedMonth(list[0]);
      else if (list.length > 0) setSelectedMonth(list[0]);
    }).catch(console.error);
  };

  useEffect(() => { refreshMonths(); }, []);

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const file = options.file as File;
    setUploading(true);
    try {
      const result = await uploadBacktestExcel(file);
      message.success(`上传成功: ${result.trade_month}, ${result.trade_rows} 条交易数据`);
      uploadKey.current += 1;
      refreshMonths();
      setSelectedMonth(result.trade_month);
      options.onSuccess?.(result);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '上传失败';
      message.error(msg);
      options.onError?.(e as Error);
    } finally {
      setUploading(false);
    }
  };

  const { days, loading } = useMediumTermData(selectedMonth);

  const windowMetas = useMemo(() => {
    const metas: { index: number; trade_date: string | null; window_name: string | null }[] = [];
    const seen = new Set<number>();
    days.forEach((d) => {
      d.windows.forEach((w) => {
        if (!seen.has(w.window_index)) {
          seen.add(w.window_index);
          metas.push({ index: w.window_index, trade_date: w.trade_date, window_name: w.window_name });
        }
      });
    });
    return metas.sort((a, b) => a.index - b.index);
  }, [days]);

  // ── ECharts 折线图配置 ──
  const chartOption: EChartsOption | null = useMemo(() => {
    if (days.length === 0) return null;

    const dates = days.map((d) => {
      const dt = new Date(d.target_date);
      return `${dt.getMonth() + 1}/${dt.getDate()}`;
    });

    const series: EChartsOption['series'] = windowMetas.map((wm, i) => {
      const data = days.map((d) => {
        const w = d.windows.find((x) => x.window_index === wm.index);
        return w ? w.price : null;
      });
      return {
        name: wm.window_name || `W${wm.index}`,
        type: 'line',
        data,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, color: WINDOW_COLORS[i % WINDOW_COLORS.length] },
        itemStyle: { color: WINDOW_COLORS[i % WINDOW_COLORS.length] },
        connectNulls: false,
        emphasis: { focus: 'series' },
      };
    });

    // 实际加权均价线
    const actualData = days.map((d) => d.actual_price);
    series.push({
      name: '实时加权均价',
      type: 'line',
      data: actualData,
      smooth: true,
      symbol: 'diamond',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: '#1677ff', type: 'dashed' },
      itemStyle: { color: '#1677ff' },
      emphasis: { focus: 'series' },
      z: 10,
    });

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#fff',
        borderColor: '#e8e8e8',
        textStyle: { color: '#333', fontSize: 13 },
        formatter: (params: any) => {
          if (!params || params.length === 0) return '';
          const dateLabel = params[0].axisValue;
          const dayData = days.find((d) => {
            const dt = new Date(d.target_date);
            return `${dt.getMonth() + 1}/${dt.getDate()}` === dateLabel;
          });
          if (!dayData) return '';

          const actualPrice = dayData.actual_price;

          let html = `<div style="font-weight:700;margin-bottom:6px;font-size:14px">标的日: ${dateLabel}</div>`;

          // 实时加权均价
          if (actualPrice != null) {
            html += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#1677ff"></span>
              <span>实时加权均价: <b>${actualPrice.toFixed(2)}</b></span>
            </div>`;
          }

          html += '<div style="border-top:1px solid #eee;margin:6px 0"></div>';

          // 各窗口
          dayData.windows.forEach((w) => {
            const above = actualPrice != null && w.price > actualPrice;
            const below = actualPrice != null && w.price < actualPrice;
            const arrow = above ? '▲' : below ? '▼' : '';
            const color = above ? '#cf1322' : below ? '#3f8600' : '#333';
            const volStr = w.volume >= 10000
              ? `${(w.volume / 10000).toFixed(1)}万`
              : w.volume.toFixed(0);
            html += `<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:${color}">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${WINDOW_COLORS[(w.window_index - 1) % WINDOW_COLORS.length]}"></span>
              <span>${w.window_name || `W${w.window_index}`}: <b>${w.price.toFixed(2)}</b> ${arrow} · 成交量 ${volStr} MWh</span>
            </div>`;
          });

          return html;
        },
      },
      legend: {
        bottom: 0,
        type: 'scroll',
        icon: 'roundRect',
        itemWidth: 14,
        itemHeight: 10,
        textStyle: { fontSize: 12 },
      },
      grid: { left: 55, right: 30, top: 20, bottom: windowMetas.length > 6 ? 60 : 45, containLabel: true },
      xAxis: {
        type: 'category',
        data: dates,
        boundaryGap: false,
        axisLabel: { fontSize: 11, rotate: days.length > 20 ? 45 : 0 },
      },
      yAxis: {
        type: 'value',
        name: '元/MWh',
        nameLocation: 'middle',
        nameGap: 42,
        nameTextStyle: { fontSize: 12 },
        axisLabel: { fontSize: 11 },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series,
    };
  }, [days, windowMetas]);

  // ── 表格列 ──
  const columns: ColumnsType<TableRow> = useMemo(() => {
    const cols: ColumnsType<TableRow> = [
      {
        title: '标的日',
        dataIndex: 'target_date',
        key: 'target_date',
        width: 100,
        fixed: 'left',
        render: (val: string) => {
          const d = new Date(val);
          return `${d.getMonth() + 1}/${d.getDate()}`;
        },
      },
    ];

    windowMetas.forEach((wm) => {
      const priceKey = `w${wm.index}_price`;
      const volKey = `w${wm.index}_vol`;
      const label = wm.window_name || `W${wm.index}`;
      const dateLabel = wm.trade_date ? wm.trade_date.slice(5) : '';
      cols.push({
        title: (
          <div style={{ textAlign: 'center' }}>
            <div>{label}</div>
            {dateLabel && <div style={{ fontSize: 11, fontWeight: 400, color: '#999' }}>{dateLabel}</div>}
          </div>
        ),
        key: `w${wm.index}`,
        width: 110,
        align: 'right',
        render: (_: unknown, record: TableRow) => {
          const price = record[priceKey] as number | undefined;
          const vol = record[volKey] as number | undefined;
          const actual = record.actual_price as number | null;
          if (price == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
          const above = actual != null && price > actual;
          const below = actual != null && price < actual;
          const textColor = above ? '#cf1322' : below ? '#3f8600' : '#333';
          return (
            <div>
              <span style={{ fontWeight: 600, color: textColor }}>{price.toFixed(2)}</span>
              {vol != null && (
                <div style={{ fontSize: 11, color: '#bbb', marginTop: 1 }}>
                  {vol >= 10000 ? `${(vol / 10000).toFixed(1)}万` : vol.toFixed(0)}
                </div>
              )}
            </div>
          );
        },
      });
    });

    cols.push({
      title: '实际均价',
      dataIndex: 'actual_price',
      key: 'actual_price',
      width: 110,
      align: 'right',
      render: (val: number | null) => {
        if (val == null) return <Tag color="default">暂无</Tag>;
        return <span style={{ fontWeight: 700, color: '#1677ff' }}>{val.toFixed(2)}</span>;
      },
    });

    cols.push({
      title: '价差(实际-W1)',
      key: 'spread',
      width: 120,
      align: 'right',
      render: (_: unknown, record: TableRow) => {
        const w1 = record.w1_price as number | undefined;
        const actual = record.actual_price as number | null;
        if (w1 == null || actual == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
        const spread = actual - w1;
        return (
          <span style={{ color: spread >= 0 ? '#cf1322' : '#3f8600', fontWeight: 600 }}>
            {spread >= 0 ? '+' : ''}{spread.toFixed(2)}
          </span>
        );
      },
    });

    return cols;
  }, [windowMetas]);

  const dataSource: TableRow[] = useMemo(() => {
    return days.map((d) => {
      const row: TableRow = {
        key: d.target_date,
        target_date: d.target_date,
        actual_price: d.actual_price,
      };
      d.windows.forEach((w) => {
        row[`w${w.window_index}_price`] = w.price;
        row[`w${w.window_index}_vol`] = w.volume;
      });
      return row;
    });
  }, [days]);

  const scrollX = 100 + windowMetas.length * 110 + 240;

  return (
    <PageContainer title="中长期交易数据">
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: isMobile ? '12px' : '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Upload
            key={uploadKey.current}
            customRequest={handleUpload}
            showUploadList={false}
            accept=".xlsx"
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              上传 Excel 母表
            </Button>
          </Upload>
          <span style={{ fontWeight: 500 }}>交易月份:</span>
          <Select
            value={selectedMonth || undefined}
            onChange={setSelectedMonth}
            style={{ width: 160 }}
            options={months.map((m) => ({ value: m, label: m }))}
            placeholder={months.length === 0 ? '请先上传数据' : '选择月份'}
            notFoundContent="无可用月份"
          />
          {days.length > 0 && (
            <span style={{ color: '#666', marginLeft: 16 }}>
              {days.length} 个标的日 · {windowMetas.length} 个交易窗口
            </span>
          )}
        </div>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <ChartCard
          title="各窗口成交电价 vs 实时加权均价"
          option={chartOption || {}}
          loading={loading}
          height={isMobile ? 280 : 420}
        />
      </div>

      <Card>
        {days.length === 0 && !loading ? (
          <Empty description="请先上传该月份的交易数据" />
        ) : (
          <Table
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: scrollX }}
            size="middle"
            bordered
            pagination={false}
            sticky
          />
        )}
      </Card>
    </PageContainer>
  );
}
